import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Star, Shield, Zap, TrendingUp, DollarSign, Users, Clock, Award } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const SEOHead = ({ title, description }) => {
  useEffect(() => {
    document.title = title;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', description);
  }, [title, description]);
  return null;
};

const ComparisonTable = ({ data }) => (
  <div className="overflow-x-auto rounded-2xl border border-white/10">
    <table className="w-full text-sm">
      <thead><tr className="bg-white/5 border-b border-white/10">
        {data.headers.map((h, i) => <th key={i} className={`p-3 sm:p-4 text-left text-xs uppercase text-zinc-400 ${i === 0 ? '' : 'text-center'}`}>{h}</th>)}
      </tr></thead>
      <tbody>{data.rows.map((row, i) => (
        <tr key={i} className={`border-b border-white/5 ${row.highlight ? 'bg-brand-primary/5' : ''}`}>
          {row.cells.map((cell, j) => <td key={j} className={`p-3 sm:p-4 ${j === 0 ? 'text-white font-semibold' : 'text-center text-zinc-300'} ${row.highlight && j === 0 ? 'text-brand-primary' : ''}`}>{cell}</td>)}
        </tr>
      ))}</tbody>
    </table>
  </div>
);

const CTABlock = ({ text }) => (
  <div className="my-8 sm:my-12 p-6 sm:p-10 bg-gradient-to-r from-brand-primary/10 to-[#7B61FF]/10 border border-brand-primary/20 rounded-2xl text-center">
    <h3 className="text-xl sm:text-2xl font-bold text-white mb-3">{text || 'Ready to Get Funded?'}</h3>
    <p className="text-zinc-400 mb-6 text-sm">Start your evaluation challenge today. Use code <span className="text-brand-primary font-bold">AYRONN20</span> for 20% off.</p>
    <Link to="/pricing" className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-brand-primary text-black font-bold rounded-xl glow-primary text-sm sm:text-base">Start Challenge <ArrowRight className="w-4 h-4" /></Link>
  </div>
);

const FAQSection = ({ faqs }) => (
  <div className="space-y-4">
    <h2 className="text-xl sm:text-2xl font-bold text-white mb-6">Frequently Asked Questions</h2>
    {faqs.map((f, i) => (
      <div key={i} className="p-5 bg-[#0A0F1C]/60 border border-white/5 rounded-xl">
        <h3 className="text-base font-semibold text-white mb-2">{f.q}</h3>
        <p className="text-zinc-400 text-sm leading-relaxed">{f.a}</p>
      </div>
    ))}
  </div>
);

// ═══════════════════════════════════════
// PAGE 1: Best Prop Firm for Beginners
// ═══════════════════════════════════════
export const BestPropFirmBeginners = () => (
  <div className="min-h-screen bg-[#050505]">
    <SEOHead title="Best Prop Firm for Beginners 2026 — AYRONN Funding | Start from $29" description="Looking for the best prop firm for beginners? AYRONN Funding offers the easiest evaluation with 85% pass rate, daily payouts, and accounts starting from $29." />
    <Navbar />
    <section className="pt-24 sm:pt-28 pb-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <span className="text-brand-primary text-xs font-bold uppercase tracking-wider">Guide</span>
          <h1 className="text-2xl sm:text-4xl lg:text-5xl font-black text-white mt-2 mb-4 leading-tight">Best Prop Firm for Beginners in 2026</h1>
          <p className="text-zinc-400 text-sm sm:text-base leading-relaxed">If you're new to prop trading, choosing the right firm can make or break your career. This guide covers everything beginners need to know about proprietary trading firms and why AYRONN Funding is the #1 choice for new traders.</p>
        </div>

        <div className="prose prose-invert max-w-none space-y-6 text-sm sm:text-base text-zinc-300 leading-relaxed">
          <h2 className="text-xl sm:text-2xl font-bold text-white mt-10">What is a Prop Trading Firm?</h2>
          <p>A proprietary trading firm (prop firm) provides capital to traders who pass an evaluation challenge. Instead of risking your own money, you trade the firm's capital and keep a percentage of the profits — typically 70-80%.</p>
          <p>For beginners, this is the fastest way to access significant trading capital without years of saving. With as little as $29, you can start an evaluation for a $5,000 funded account.</p>

          <h2 className="text-xl sm:text-2xl font-bold text-white mt-10">Why AYRONN Funding is Best for Beginners</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 not-prose my-6">
            {[{t:'85% Pass Rate',d:'Industry highest — designed for traders to succeed'},{t:'Accounts from $29',d:'Lowest entry point in the market'},{t:'Daily Payouts',d:'Get your profits every single day'},{t:'Simple Rules',d:'Clear, transparent trading guidelines'},{t:'Instant Activation',d:'Start trading within minutes of purchase'},{t:'Crypto Payments',d:'Pay with 50+ cryptocurrencies'}].map((f,i) => (
              <div key={i} className="flex items-start gap-3 p-4 bg-white/[0.03] rounded-xl border border-white/5"><CheckCircle2 className="w-5 h-5 text-brand-positive flex-shrink-0 mt-0.5" /><div><div className="text-white font-semibold text-sm">{f.t}</div><div className="text-zinc-500 text-xs">{f.d}</div></div></div>
            ))}
          </div>

          <h2 className="text-xl sm:text-2xl font-bold text-white mt-10">AYRONN vs Other Prop Firms — Comparison</h2>
        </div>

        <ComparisonTable data={{
          headers: ['Feature', 'AYRONN Funding', 'FTMO', 'FundedNext', 'MyForexFunds'],
          rows: [
            { cells: ['Starting Price', '$29', '$155', '$32', '$49'], highlight: true },
            { cells: ['Pass Rate', '85%', '~10%', '~15%', '~12%'], highlight: true },
            { cells: ['Payout Frequency', 'Daily', 'Bi-weekly', 'Bi-weekly', 'Monthly'] },
            { cells: ['Profit Split', 'Up to 80%', 'Up to 90%', 'Up to 80%', 'Up to 75%'] },
            { cells: ['Instant Funding', 'Yes', 'No', 'Yes', 'No'], highlight: true },
            { cells: ['Crypto Payments', 'Yes (50+)', 'No', 'Limited', 'No'] },
            { cells: ['Account Sizes', '$5K-$100K', '$10K-$200K', '$6K-$200K', '$5K-$300K'] },
          ]
        }} />

        <CTABlock text="Start Your Beginner-Friendly Challenge" />

        <div className="prose prose-invert max-w-none space-y-6 text-sm sm:text-base text-zinc-300 leading-relaxed">
          <h2 className="text-xl sm:text-2xl font-bold text-white mt-10">Step-by-Step: How to Start as a Beginner</h2>
          <ol className="space-y-3">
            <li><strong className="text-white">Choose Your Challenge Type:</strong> 2-Step is recommended for beginners — lower cost, relaxed targets.</li>
            <li><strong className="text-white">Select Account Size:</strong> Start with $5K or $10K to minimize risk while learning.</li>
            <li><strong className="text-white">Pass the Evaluation:</strong> Hit the profit target while staying within drawdown limits.</li>
            <li><strong className="text-white">Get Funded:</strong> Receive your funded account credentials and start earning.</li>
            <li><strong className="text-white">Request Payouts:</strong> Withdraw your profits daily — no waiting periods.</li>
          </ol>

          <h2 className="text-xl sm:text-2xl font-bold text-white mt-10">Common Beginner Mistakes to Avoid</h2>
          <ul className="space-y-2">
            <li>Over-leveraging: Start with smaller position sizes until you're comfortable.</li>
            <li>Ignoring risk management: The drawdown limit exists for a reason.</li>
            <li>Trading without a plan: Define your strategy before placing any trades.</li>
            <li>Choosing the wrong account size: Start small, scale up after success.</li>
          </ul>
        </div>

        <FAQSection faqs={[
          {q:'What is the best prop firm for beginners in 2026?',a:'AYRONN Funding is widely considered the best prop firm for beginners due to its 85% pass rate, low starting price of $29, and simple evaluation rules.'},
          {q:'How much money do I need to start?',a:'You can start with as little as $29 for a 2-Step $5K challenge. This is the lowest entry point among major prop firms.'},
          {q:'Can beginners really make money with prop firms?',a:'Yes. Many beginners pass their evaluation within the first month. With an 85% pass rate, AYRONN Funding is designed for traders to succeed.'},
          {q:'Do I need trading experience?',a:'While some experience helps, AYRONN Funding evaluation rules are beginner-friendly. Practice on a demo account first if you are completely new.'},
        ]} />

        <CTABlock />
      </div>
    </section>
    <Footer />
  </div>
);

// ═══════════════════════════════════════
// PAGE 2: Instant Funding Prop Firm
// ═══════════════════════════════════════
export const InstantFundingPropFirm = () => (
  <div className="min-h-screen bg-[#050505]">
    <SEOHead title="Instant Funding Prop Firm 2026 — Skip the Challenge | AYRONN Funding" description="Get instant funded accounts with no evaluation phase. AYRONN Funding offers instant funding from $99 with daily payouts and 80% profit split." />
    <Navbar />
    <section className="pt-24 sm:pt-28 pb-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <span className="text-brand-primary text-xs font-bold uppercase tracking-wider">Instant Funding</span>
        <h1 className="text-2xl sm:text-4xl lg:text-5xl font-black text-white mt-2 mb-4 leading-tight">Instant Funding Prop Firm — No Evaluation Required</h1>
        <p className="text-zinc-400 mb-8 text-sm sm:text-base">Skip the evaluation phase entirely. Get a funded trading account instantly with AYRONN Funding's Instant program.</p>

        <div className="prose prose-invert max-w-none space-y-6 text-sm sm:text-base text-zinc-300 leading-relaxed">
          <h2 className="text-xl sm:text-2xl font-bold text-white">What is Instant Funding?</h2>
          <p>Instant funding means you receive a funded trading account immediately after purchase — no evaluation challenges, no profit targets to hit first, no waiting period. You start trading with real capital from day one.</p>
          <p>AYRONN Funding's Instant program offers accounts from $10K to $100K with an 80% profit split and daily payout options.</p>

          <h2 className="text-xl sm:text-2xl font-bold text-white mt-10">Instant Funding Plans & Pricing</h2>
        </div>

        <ComparisonTable data={{
          headers: ['Account Size', 'Price', 'Max Drawdown', 'Daily DD', 'Profit Split'],
          rows: [
            { cells: ['$10,000', '$99', '7%', '4%', '80%'] },
            { cells: ['$25,000', '$179', '7%', '4%', '80%'] },
            { cells: ['$50,000', '$289', '7%', '4%', '80%'], highlight: true },
            { cells: ['$100,000', '$449', '7%', '4%', '80%'] },
          ]
        }} />

        <CTABlock text="Get Instant Funding Now" />

        <div className="prose prose-invert max-w-none space-y-6 text-sm sm:text-base text-zinc-300 leading-relaxed">
          <h2 className="text-xl sm:text-2xl font-bold text-white mt-10">Who Should Choose Instant Funding?</h2>
          <ul className="space-y-2">
            <li><strong className="text-white">Experienced traders</strong> who don't want to waste time on evaluations</li>
            <li><strong className="text-white">Profitable traders</strong> who want immediate access to capital</li>
            <li><strong className="text-white">Strategy testers</strong> who want to validate live performance quickly</li>
          </ul>
        </div>

        <FAQSection faqs={[
          {q:'Do I need to pass an evaluation for instant funding?',a:'No. With AYRONN Funding Instant program, you receive your funded account immediately after purchase.'},
          {q:'What are the rules for instant funded accounts?',a:'Instant accounts have a 7% max drawdown, 4% daily drawdown, and a consistency rule. No profit targets required.'},
          {q:'Can I get daily payouts with instant funding?',a:'Yes! AYRONN Funding offers daily payouts on all account types including instant funded accounts.'},
        ]} />
        <CTABlock />
      </div>
    </section>
    <Footer />
  </div>
);

// ═══════════════════════════════════════
// PAGE 3: Prop Firm India
// ═══════════════════════════════════════
export const PropFirmIndia = () => (
  <div className="min-h-screen bg-[#050505]">
    <SEOHead title="Best Prop Firm in India 2026 — AYRONN Funding | Crypto Payments Accepted" description="Best prop trading firm for Indian traders. AYRONN Funding accepts crypto payments, offers INR-friendly pricing from $29, and daily payouts." />
    <Navbar />
    <section className="pt-24 sm:pt-28 pb-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <span className="text-brand-primary text-xs font-bold uppercase tracking-wider">India</span>
        <h1 className="text-2xl sm:text-4xl lg:text-5xl font-black text-white mt-2 mb-4 leading-tight">Best Prop Firm for Indian Traders in 2026</h1>
        <p className="text-zinc-400 mb-8 text-sm sm:text-base">AYRONN Funding is the #1 choice for Indian traders looking to access funded capital without restrictions.</p>

        <div className="prose prose-invert max-w-none space-y-6 text-sm sm:text-base text-zinc-300 leading-relaxed">
          <h2 className="text-xl sm:text-2xl font-bold text-white">Why Indian Traders Choose AYRONN Funding</h2>
          <p>India has one of the fastest-growing trading communities in the world. However, many global prop firms either don't accept Indian traders or make it difficult with payment restrictions. AYRONN Funding solves this completely.</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 not-prose my-6">
            {[{t:'Crypto Payments',d:'Pay with UPI → crypto → AYRONN. Easy for Indian traders.'},{t:'Low Entry at $29',d:'Approx ₹2,400 — most affordable prop firm'},{t:'No Geo Restrictions',d:'Full access from India, no KYC hassles'},{t:'Daily Payouts',d:'Receive profits in crypto, convert to INR instantly'}].map((f,i) => (
              <div key={i} className="flex items-start gap-3 p-4 bg-white/[0.03] rounded-xl border border-white/5"><CheckCircle2 className="w-5 h-5 text-brand-positive flex-shrink-0 mt-0.5" /><div><div className="text-white font-semibold text-sm">{f.t}</div><div className="text-zinc-500 text-xs">{f.d}</div></div></div>
            ))}
          </div>

          <h2 className="text-xl sm:text-2xl font-bold text-white mt-10">How to Pay from India</h2>
          <ol className="space-y-2">
            <li>Buy USDT on any Indian crypto exchange (WazirX, CoinDCX, Binance P2P)</li>
            <li>Select your AYRONN Funding plan and proceed to checkout</li>
            <li>Pay with USDT (TRC20) or 50+ other cryptocurrencies</li>
            <li>Account activated instantly — start trading</li>
          </ol>
        </div>

        <CTABlock text="Start Trading from India — ₹2,400 Only" />

        <FAQSection faqs={[
          {q:'Can Indian traders join AYRONN Funding?',a:'Yes! AYRONN Funding accepts traders from India with no restrictions. Payments are accepted via 50+ cryptocurrencies.'},
          {q:'How much does it cost in INR?',a:'The cheapest plan starts at $29 (approximately ₹2,400). Use code AYRONN20 for 20% off.'},
          {q:'How do I receive payouts in India?',a:'Payouts are sent to your crypto wallet. You can convert to INR via any Indian crypto exchange.'},
          {q:'Is prop firm trading legal in India?',a:'AYRONN Funding provides simulated trading evaluation services. Traders participate in evaluation programs, not direct market investment.'},
        ]} />
        <CTABlock />
      </div>
    </section>
    <Footer />
  </div>
);

// ═══════════════════════════════════════
// PAGE 4: Cheapest Prop Firm
// ═══════════════════════════════════════
export const CheapestPropFirm = () => (
  <div className="min-h-screen bg-[#050505]">
    <SEOHead title="Cheapest Prop Firm 2026 — Start from $29 | AYRONN Funding" description="Find the cheapest prop firm in 2026. AYRONN Funding offers trading challenges from just $29 with the highest pass rate in the industry." />
    <Navbar />
    <section className="pt-24 sm:pt-28 pb-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <span className="text-brand-primary text-xs font-bold uppercase tracking-wider">Affordable</span>
        <h1 className="text-2xl sm:text-4xl lg:text-5xl font-black text-white mt-2 mb-4 leading-tight">Cheapest Prop Firm in 2026 — Start Trading for $29</h1>
        <p className="text-zinc-400 mb-8 text-sm sm:text-base">Don't overpay for prop firm challenges. AYRONN Funding offers the most affordable path to funded trading.</p>

        <div className="prose prose-invert max-w-none space-y-6 text-sm sm:text-base text-zinc-300 leading-relaxed">
          <h2 className="text-xl sm:text-2xl font-bold text-white">Cheapest Prop Firm Plans — Price Comparison</h2>
        </div>

        <ComparisonTable data={{
          headers: ['Firm', '$5K Account', '$10K Account', '$25K Account', '$50K Account'],
          rows: [
            { cells: ['AYRONN Funding', '$29', '$59', '$129', '$219'], highlight: true },
            { cells: ['FTMO', 'N/A', '$155', '$250', '$345'] },
            { cells: ['FundedNext', '$32', '$59', '$129', '$229'] },
            { cells: ['The5ers', 'N/A', '$95', '$225', '$350'] },
          ]
        }} />

        <CTABlock text="Get the Cheapest Challenge — Use AYRONN20" />

        <div className="prose prose-invert max-w-none space-y-6 text-sm sm:text-base text-zinc-300 leading-relaxed">
          <h2 className="text-xl sm:text-2xl font-bold text-white mt-10">Why Cheap Doesn't Mean Low Quality</h2>
          <p>AYRONN Funding keeps prices low by using efficient crypto payments, automated account management, and a technology-first approach. This means lower overhead, lower fees, and more value for traders.</p>
          <p>Despite being the cheapest, AYRONN Funding offers the highest pass rate (85%), daily payouts, and 80% profit split — features that many expensive firms don't match.</p>
        </div>

        <FAQSection faqs={[
          {q:'What is the cheapest prop firm in 2026?',a:'AYRONN Funding offers the cheapest prop firm challenges starting from just $29 for a $5,000 2-Step evaluation.'},
          {q:'Is the cheapest prop firm also the best?',a:'AYRONN Funding proves that affordable and quality can coexist. With an 85% pass rate and daily payouts, it outperforms many expensive alternatives.'},
          {q:'Can I use a coupon code?',a:'Yes! Use code AYRONN20 at checkout for an additional 20% discount on any plan.'},
        ]} />
        <CTABlock />
      </div>
    </section>
    <Footer />
  </div>
);

// ═══════════════════════════════════════
// PAGE 5: Highest Payout Prop Firm
// ═══════════════════════════════════════
export const HighestPayoutPropFirm = () => (
  <div className="min-h-screen bg-[#050505]">
    <SEOHead title="Highest Payout Prop Firm 2026 — Daily Payouts | AYRONN Funding" description="Get the highest and fastest payouts from a prop firm. AYRONN Funding offers daily payouts with 80% profit split and $2.5M+ total paid." />
    <Navbar />
    <section className="pt-24 sm:pt-28 pb-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <span className="text-brand-primary text-xs font-bold uppercase tracking-wider">Payouts</span>
        <h1 className="text-2xl sm:text-4xl lg:text-5xl font-black text-white mt-2 mb-4 leading-tight">Highest Payout Prop Firm — Daily Withdrawals</h1>
        <p className="text-zinc-400 mb-8 text-sm sm:text-base">AYRONN Funding leads the industry with daily payouts and over $2.5M paid to traders worldwide.</p>

        <div className="prose prose-invert max-w-none space-y-6 text-sm sm:text-base text-zinc-300 leading-relaxed">
          <h2 className="text-xl sm:text-2xl font-bold text-white">Why Payout Speed Matters</h2>
          <p>Most prop firms make you wait 14-30 days for payouts. This creates cash flow problems for full-time traders. AYRONN Funding disrupts this with <strong className="text-brand-primary">daily payouts</strong> — request your profits any day and receive them within 24 hours.</p>

          <h2 className="text-xl sm:text-2xl font-bold text-white mt-10">Payout Comparison</h2>
        </div>

        <ComparisonTable data={{
          headers: ['Feature', 'AYRONN Funding', 'FTMO', 'FundedNext', 'MyForexFunds'],
          rows: [
            { cells: ['Payout Frequency', 'DAILY', 'Bi-weekly', 'Bi-weekly', 'Monthly'], highlight: true },
            { cells: ['Profit Split', '80%', '80-90%', '80%', '75%'] },
            { cells: ['Processing Time', '<24 hours', '1-3 days', '3-5 days', '5-7 days'], highlight: true },
            { cells: ['Payment Method', 'Crypto (50+)', 'Bank/Crypto', 'Bank/Crypto', 'Bank only'] },
            { cells: ['Minimum Payout', '$50', '$100', '$100', '$100'] },
            { cells: ['Total Paid Out', '$2.5M+', '$100M+', '$40M+', 'N/A'] },
          ]
        }} />

        <CTABlock text="Start Earning Daily Payouts" />

        <FAQSection faqs={[
          {q:'Which prop firm has the highest payouts?',a:'AYRONN Funding offers the highest payout frequency with daily withdrawals. No other major prop firm matches this speed.'},
          {q:'How fast do I receive my payout?',a:'Payouts are processed within 24 hours of your request and sent directly to your crypto wallet.'},
          {q:'What is the profit split?',a:'AYRONN Funding offers an 80% profit split on all funded accounts.'},
        ]} />
        <CTABlock />
      </div>
    </section>
    <Footer />
  </div>
);

// ═══════════════════════════════════════
// BLOG INDEX
// ═══════════════════════════════════════
export const BlogIndex = () => (
  <div className="min-h-screen bg-[#050505]">
    <SEOHead title="AYRONN Funding Blog — Prop Trading Tips, Strategies & News 2026" description="Learn prop trading strategies, risk management, and how to pass your funded trader evaluation. Expert tips from AYRONN Funding." />
    <Navbar />
    <section className="pt-24 sm:pt-28 pb-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-2xl sm:text-4xl font-black text-white mb-8">Trading Blog</h1>
        <div className="space-y-6">
          {[
            {title:'How to Pass a Prop Firm Challenge in 2026: Complete Guide',slug:'/best-prop-firm-for-beginners',tag:'Guide'},
            {title:'Instant Funding vs Evaluation: Which is Better?',slug:'/instant-funding-prop-firm',tag:'Comparison'},
            {title:'Best Prop Firm for Indian Traders — Complete Guide',slug:'/prop-firm-india',tag:'Regional'},
            {title:'Cheapest Prop Firms Ranked — 2026 Price Comparison',slug:'/cheapest-prop-firm',tag:'Pricing'},
            {title:'Daily Payouts: Why AYRONN Funding Pays Faster Than Anyone',slug:'/highest-payout-prop-firm',tag:'Payouts'},
          ].map((post, i) => (
            <Link key={i} to={post.slug} className="block p-5 sm:p-6 bg-[#0A0F1C]/60 border border-white/5 rounded-2xl hover:border-brand-primary/30 transition-all group">
              <span className="text-brand-primary text-[10px] font-bold uppercase tracking-wider">{post.tag}</span>
              <h2 className="text-lg sm:text-xl font-bold text-white mt-1 group-hover:text-brand-primary transition-colors">{post.title}</h2>
            </Link>
          ))}
        </div>
      </div>
    </section>
    <Footer />
  </div>
);
